/**
 * ClaimReviewStep
 *
 * Review and confirmation workspace for client and coverage fields. Displays
 * a checklist for required fields, a coverage context panel, and an evidence
 * pane which shows citations/artifacts for the currently focused field.
 * Supports hover-to-highlight linkage between left and right panes.
 */
import {
  Check,
  ChevronDown,
  MessageSquare,
  Plus,
  Search,
  Sparkles,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AgentChatProvider,
  AgentChatThread,
  useAgentChat,
} from '@/components/agent-chat';
import { EditorProvider } from '@/components/demands-v2/editor/context/editor-provider';
import { useCoverageAgentThread } from '@/hooks/useCoverageAgentThread';
import {
  useCreateAgentChatThreadMutation,
  useListAgentChatThreadsQuery,
} from '@/services/api/agentChatService';
import {
  useGetCoverageQuery,
  useRunOpenClaimDiscoveryMutation,
} from '@/services/api/coverageService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { CoverageSchema } from '@/services/types/coverage-types';
import {
  DataRequirement,
  DataRequirementValue,
  DataRequirementValueContext,
} from '@/services/types/data-requirements-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { InsuranceInfoSection } from '../common/InsuranceInfoSection';
import { VehicleInfoSection } from '../common/VehicleInfoSection';
import { CoverageType, ReviewedFieldState } from '../hooks/useClaimFilingState';
import {
  AIExplanationPane,
  type AIExtractionContext,
} from './AIExplanationPane';
import { ClientInfoSection } from './ClientInfoSection';
import { EvidencePane } from './EvidencePane';
import { type FieldSourceInfo, ReviewFieldsPanel } from './ReviewFieldsPanel';
import { computeInitialSearchTerm } from './utils';

/** Props for `ClaimReviewStep`. */
interface ClaimReviewStepProps {
  /** Current matter id. */
  matterId: string;
  /** Current coverage id. */
  coverageId: string;
  /** Optional pre-fetched coverage to avoid duplicate query. */
  linkedCoverage?: CoverageSchema;
  /** List of fields to review with status/values. */
  reviewedFields: ReviewedFieldState[];
  /** Requirement metadata keyed by field for evidence derivation. */
  requirementsById?: Record<string, DataRequirement>;
  /** Selected coverage side ('1P' | '3P'). */
  coverageType: CoverageType;
  /** Whether this coverage is third-party auto; used to auto-review. */
  isThirdPartyAuto: boolean;
  /** Currently highlighted field key (for evidence pane). */
  activeFieldKey: string | null;
  /** Setter for highlighted field key. */
  setActiveFieldKey: (key: string | null) => void;
  /** Mark a field as reviewed. */
  markReviewed: (key: string) => void;
  /** Modify a field's value. */
  modifyField: (key: string, value: string) => void;
  /** Confirm a specific DR value for a requirement */
  onConfirmValue?: (requirementId: string, valueId: string) => void;
}

// Inner component that uses AgentChatProvider context
const ClaimReviewStepInner: React.FC<ClaimReviewStepProps> = ({
  matterId,
  coverageId,
  linkedCoverage,
  reviewedFields,
  requirementsById,
  coverageType,
  isThirdPartyAuto,
  activeFieldKey,
  setActiveFieldKey,
  markReviewed,
  modifyField,
  onConfirmValue,
}) => {
  const { setSelectedThreadId } = useAgentChat();
  const [createThread, createThreadState] = useCreateAgentChatThreadMutation();

  // Track the active field's source info for the evidence pane
  const [activeFieldSource, setActiveFieldSource] =
    useState<FieldSourceInfo | null>(null);

  // Track locked field - when locked, hover doesn't change active field
  const [lockedFieldKey, setLockedFieldKey] = useState<string | null>(null);

  // Handle field hover - only update active field if not locked
  const handleFieldHover = useCallback(
    (key: string | null) => {
      if (!lockedFieldKey) {
        setActiveFieldKey(key);
      }
    },
    [lockedFieldKey, setActiveFieldKey],
  );

  // Handle field click - toggle lock state
  const handleFieldClick = useCallback(
    (key: string) => {
      if (lockedFieldKey === key) {
        // Clicking same field: unlock and clear
        setLockedFieldKey(null);
      } else {
        // Clicking different field: lock to this field
        setLockedFieldKey(key);
        setActiveFieldKey(key);
      }
    },
    [lockedFieldKey, setActiveFieldKey],
  );

  // Toggle between new panel view and legacy view
  const [useNewPanel, setUseNewPanel] = useState(true);

  // Toggle between sources and chat view on right panel
  const [rightPanelView, setRightPanelView] = useState<'sources' | 'chat'>(
    'sources',
  );

  // Discovery workflow state
  const [isRunningDiscovery, setIsRunningDiscovery] = useState(false);
  const [runDiscovery] = useRunOpenClaimDiscoveryMutation();

  // Get or create agent chat thread for this coverage
  const {
    threadId: defaultThreadId,
    threads: coverageThreads,
    isLoading: isCoverageThreadsLoading,
  } = useCoverageAgentThread({
    matterId,
    coverageId,
    title: 'Open Claim Discovery',
  });

  // Also fetch ALL threads for the matter (not filtered by coverage)
  const { data: allMatterThreads = [], isLoading: isAllThreadsLoading } =
    useListAgentChatThreadsQuery({ matterId });

  const isThreadsLoading = isCoverageThreadsLoading || isAllThreadsLoading;

  // Thread selector state
  const [localSelectedThreadId, setLocalSelectedThreadId] = useState<
    string | null
  >(null);
  const [isThreadDropdownOpen, setIsThreadDropdownOpen] = useState(false);
  const threadDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        threadDropdownRef.current &&
        !threadDropdownRef.current.contains(event.target as Node)
      ) {
        setIsThreadDropdownOpen(false);
      }
    };
    if (isThreadDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isThreadDropdownOpen]);

  // Sync selected thread with default when it becomes available
  useEffect(() => {
    if (!localSelectedThreadId && defaultThreadId) {
      setLocalSelectedThreadId(defaultThreadId);
      setSelectedThreadId(defaultThreadId);
    }
  }, [defaultThreadId, localSelectedThreadId, setSelectedThreadId]);

  // Thread options - include all matter threads and newly created threads
  const threadOptions = useMemo(() => {
    // Combine all threads, prioritizing coverage-specific ones
    const threadMap = new Map<string, (typeof allMatterThreads)[0]>();

    // Add all matter threads first
    allMatterThreads.forEach((t) => threadMap.set(t.id, t));

    // Coverage threads might have different metadata, overlay them
    coverageThreads.forEach((t) => threadMap.set(t.id, t));

    // Add newly created thread if not already in the list
    if (createThreadState.data) {
      threadMap.set(createThreadState.data.id, createThreadState.data);
    }

    // Sort by created_at descending (newest first)
    return Array.from(threadMap.values()).sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bDate - aDate;
    });
  }, [allMatterThreads, coverageThreads, createThreadState.data]);

  // Get the currently selected thread
  const currentThread = useMemo(() => {
    if (!localSelectedThreadId) return null;
    return threadOptions.find((t) => t.id === localSelectedThreadId) || null;
  }, [threadOptions, localSelectedThreadId]);

  // Handle creating a new thread
  const handleCreateNewThread = useCallback(async () => {
    if (!matterId) return;
    try {
      const newThread = await createThread({
        matterId,
        data: {
          title: 'Open Claim Discovery',
          initial_task: null,
          workspace_id: null,
          coverage_id: coverageId,
        },
      }).unwrap();
      setLocalSelectedThreadId(newThread.id);
      setSelectedThreadId(newThread.id);
      setIsThreadDropdownOpen(false);
    } catch (error) {
      console.error('Failed to create new thread:', error);
    }
  }, [matterId, coverageId, createThread, setSelectedThreadId]);

  // Handle selecting a different thread
  const handleSelectThread = useCallback(
    (threadId: string) => {
      setLocalSelectedThreadId(threadId);
      setSelectedThreadId(threadId);
      setIsThreadDropdownOpen(false);
    },
    [setSelectedThreadId],
  );

  // Each section manages its own open state internally for consistency

  // Typed allowlist: we normalize display names to be robust but keep
  // compile-time safety by enumerating the normalized label union.
  const normalizeLabel = (label: string): string =>
    label.trim().toLowerCase().replace(/\s+/g, ' ');

  const ALLOWED_CLIENT_LABELS_NORMALIZED = useMemo(
    () =>
      [
        // Client DOB
        'client date of birth',
        'date of birth',
        'dob',
        // Client name
        'client name',
        'client full name',
        'name',
        // Matter location
        'matter city',
        'matter state',
        // Date of loss / incident
        'date of loss/incident',
        'date of loss',
        'incident date',
        // Client address
        'client address',
        'address',
        'mailing address',
        'home address',
        // Client SSN last four
        'client last four of ssn',
        'client last four ssn',
        'last four ssn',
        'last 4 ssn',
      ] as const,
    [],
  );

  type AllowedClientLabel = (typeof ALLOWED_CLIENT_LABELS_NORMALIZED)[number];
  const isAllowedClientLabel = useCallback(
    (label: string): label is AllowedClientLabel =>
      (ALLOWED_CLIENT_LABELS_NORMALIZED as readonly string[]).includes(label),
    [ALLOWED_CLIENT_LABELS_NORMALIZED],
  );

  const filteredReviewedFields = useMemo<ReviewedFieldState[]>(() => {
    return reviewedFields.filter((f) => {
      if (f.meta?.section !== 'client') return false; // hide non-client fields from checklist
      const normalized = normalizeLabel(f.displayName);
      return isAllowedClientLabel(normalized);
    });
  }, [reviewedFields, isAllowedClientLabel]);

  // Removed summary chip per new UI; keep computation out for performance.

  const { data: fetchedCoverage, refetch: refetchCoverage } =
    useGetCoverageQuery({
      matterId,
      coverageId,
    });

  const coverage: CoverageSchema | undefined =
    linkedCoverage ?? fetchedCoverage;

  // Handle running AI discovery workflow
  const handleRunDiscovery = useCallback(async () => {
    if (!coverageId) return;

    const initialDiscoveryTimestamp =
      coverage?.open_claim_context?.discovery_completed_at;

    setIsRunningDiscovery(true);
    setRightPanelView('chat'); // Switch to chat view to show progress

    try {
      await runDiscovery({
        matterId,
        coverageId,
        threadId: localSelectedThreadId ?? undefined,
        taskId: undefined,
      });

      // Poll for updates since the workflow runs async
      let attempts = 0;
      const maxAttempts = 20;
      const pollInterval = setInterval(async () => {
        attempts += 1;
        try {
          const result = await refetchCoverage();
          const newTimestamp =
            result.data?.open_claim_context?.discovery_completed_at;
          if (newTimestamp && newTimestamp !== initialDiscoveryTimestamp) {
            clearInterval(pollInterval);
            setIsRunningDiscovery(false);
          }
        } catch {
          // Ignore refetch errors
        }
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setIsRunningDiscovery(false);
        }
      }, 3000);
    } catch (error) {
      console.error('Failed to start discovery workflow:', error);
      setIsRunningDiscovery(false);
    }
  }, [
    matterId,
    coverageId,
    localSelectedThreadId,
    runDiscovery,
    refetchCoverage,
    coverage?.open_claim_context?.discovery_completed_at,
  ]);

  // Find the discovery task for quick navigation (Find 1P/3P Coverage)
  const { data: tasksResponse } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });
  const discoveryTask = React.useMemo(() => {
    const tasks = tasksResponse?.results || [];
    const desiredType =
      coverageType === '1P'
        ? ('Find 1P Coverage' as CanonicalTaskType)
        : ('Find 3P Coverage' as CanonicalTaskType);
    return tasks.find((t) => t.type?.canonical_type === desiredType);
  }, [tasksResponse, coverageType]);
  const isDiscoveryTaskCompleted =
    discoveryTask?.status?.canonical_status === CanonicalTaskStatus.COMPLETED;

  // Backward compatibility: old Review Found Coverage task linked to this coverage
  const isOldReviewTaskCompleted = React.useMemo(() => {
    const tasks = tasksResponse?.results || [];
    const candidates = tasks.filter(
      (t) =>
        (t.coverage_id === coverageId || t.coverage?.id === coverageId) &&
        t.type?.canonical_type === CanonicalTaskType.REVIEW_FOUND_COVERAGE,
    );
    return candidates.some(
      (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
  }, [tasksResponse, coverageId]);

  // Get the active field details for AI explanation pane
  const activeFieldDetails = useMemo(() => {
    if (!activeFieldKey) return null;

    // Find the reviewed field for display name and value
    const field = reviewedFields.find((f) => f.key === activeFieldKey);
    const fieldName = field?.displayName || activeFieldKey;
    const fieldValue = field?.value || '';
    const isRequired = field?.required || false;

    // Get extraction context from data requirements
    const req = requirementsById?.[activeFieldKey];
    let extractionContext: AIExtractionContext | null = null;
    let allExtractionContexts: AIExtractionContext[] = [];

    if (req?.values) {
      // Get all extraction contexts
      allExtractionContexts = req.values
        .filter((v: DataRequirementValue) => v.status === 'Extracted')
        .map((v: DataRequirementValue) => {
          const ctx = v.context as DataRequirementValueContext | null;
          return {
            rationale: ctx?.rationale,
            extraction_source: ctx?.extraction_source,
            confidence_notes: ctx?.confidence_notes,
            artifact_id: ctx?.artifact_id,
            excerpts: ctx?.excerpts,
            extraction_context: ctx?.extraction_context,
            citation_ids: ctx?.citation_ids,
            confidence: v.confidence,
          } as AIExtractionContext;
        })
        .filter(
          (ctx) => ctx.artifact_id || ctx.rationale || ctx.excerpts?.length,
        );

      // Primary context is the highest confidence one
      if (allExtractionContexts.length > 0) {
        const sorted = allExtractionContexts.sort(
          (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0),
        );
        [extractionContext] = sorted;
      }
    }

    return {
      fieldName,
      fieldValue,
      isRequired,
      extractionContext,
      allExtractionContexts,
    };
  }, [activeFieldKey, reviewedFields, requirementsById]);

  // Determine whether to show AI explanation (for client fields) or evidence (for coverage fields)
  const sectionType = useMemo(():
    | 'client'
    | 'vehicle'
    | 'insurance'
    | 'incident' => {
    const key = activeFieldKey || '';
    if (key.startsWith('vehicle:')) return 'vehicle';
    if (key.startsWith('ins:') || key.startsWith('insurance:'))
      return 'insurance';
    if (key.startsWith('incident:')) return 'incident';
    if (key.startsWith('at_fault:')) return 'incident'; // At-fault fields relate to incident
    return 'client';
  }, [activeFieldKey]);

  return (
    <div className="mx-auto max-w-7xl flex-1 w-full h-[calc(100vh-140px)] overflow-hidden">
      {discoveryTask ? (
        <div className="px-4 pt-3 flex items-center justify-between">
          <a
            href={`/matters/${matterId}/?task_id=${discoveryTask.id}`}
            className="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline"
          >
            ← Back to{' '}
            {coverageType === '1P' ? 'Find 1P Coverage' : 'Find 3P Coverage'}{' '}
            task
          </a>
          {(isDiscoveryTaskCompleted || isOldReviewTaskCompleted) && (
            <div className="inline-flex items-center gap-1.5 text-xs text-green-700">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white">
                <Check className="h-3 w-3" />
              </span>
              <span>Completed</span>
            </div>
          )}
        </div>
      ) : null}
      <div className="grid grid-cols-12 gap-4 p-4 h-full">
        <div className="col-span-5 overflow-y-auto h-full">
          {/* Toggle between new and legacy panel */}
          <div className="flex items-center justify-end mb-2">
            <button
              type="button"
              onClick={() => setUseNewPanel((v) => !v)}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              {useNewPanel ? 'Switch to legacy view' : 'Switch to new view'}
            </button>
          </div>

          {useNewPanel ? (
            /* New unified ReviewFieldsPanel with field-level sourcing */
            <ReviewFieldsPanel
              matterId={matterId}
              coverageId={coverageId}
              coverage={coverage}
              activeFieldKey={activeFieldKey}
              lockedFieldKey={lockedFieldKey}
              onActiveFieldKeyChange={handleFieldHover}
              onFieldClick={handleFieldClick}
              onActiveFieldSourceChange={setActiveFieldSource}
            />
          ) : (
            /* Legacy view with separate sections */
            <div className="space-y-5 pb-12">
              {!(
                coverage?.carrier?.handles &&
                (coverage.carrier.handles as unknown as Array<unknown>)?.length
              ) && (
                <div className="rounded border border-red-200 bg-red-50 p-2">
                  <div className="text-xs font-semibold text-red-800">
                    Missing carrier contact
                  </div>
                  <div className="text-[11px] text-red-700 mt-1">
                    <p>
                      Attach a carrier contact handle (phone, email, fax,
                      website) to this carrier.
                    </p>
                    <p>
                      Use the Carrier Directory to select the correct division
                      and handles.
                    </p>
                  </div>
                </div>
              )}
              {!isThirdPartyAuto && (
                <ClientInfoSection
                  matterId={matterId}
                  reviewedFields={filteredReviewedFields}
                  onAccept={markReviewed}
                  onModify={modifyField}
                  onHoverField={setActiveFieldKey}
                  onSelectField={setActiveFieldKey as (key: string) => void}
                  activeFieldKey={activeFieldKey}
                  requirementsById={requirementsById}
                  onConfirmValue={onConfirmValue}
                />
              )}
              {linkedCoverage && (
                <>
                  <VehicleInfoSection
                    matterId={matterId}
                    coverage={linkedCoverage}
                    onHoverField={setActiveFieldKey}
                  />
                  <InsuranceInfoSection
                    matterId={matterId}
                    coverage={linkedCoverage}
                    onHoverField={setActiveFieldKey}
                  />
                </>
              )}
            </div>
          )}
        </div>

        <div className="col-span-7 h-full flex flex-col min-h-0 bg-gray-50/30 rounded-lg">
          {/* Header with toggle and Run Discovery button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setRightPanelView('sources')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  rightPanelView === 'sources'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Search size={14} />
                Sources
              </button>
              <button
                type="button"
                onClick={() => setRightPanelView('chat')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  rightPanelView === 'chat'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MessageSquare size={14} />
                Agent Chat
              </button>

              {/* Thread selector dropdown - only show in chat view */}
              {rightPanelView === 'chat' && (
                <div className="relative ml-2" ref={threadDropdownRef}>
                  <button
                    type="button"
                    onClick={() =>
                      setIsThreadDropdownOpen(!isThreadDropdownOpen)
                    }
                    disabled={isThreadsLoading}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 rounded transition-colors border border-zinc-200"
                  >
                    <span className="max-w-[150px] truncate">
                      {currentThread?.title || 'Select thread'}
                    </span>
                    <ChevronDown size={14} />
                  </button>
                  {isThreadDropdownOpen && (
                    <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-zinc-200 rounded-lg shadow-lg z-50">
                      <div className="max-h-48 overflow-y-auto py-1">
                        {threadOptions.map((thread) => (
                          <button
                            key={thread.id}
                            type="button"
                            onClick={() => handleSelectThread(thread.id)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 transition-colors ${
                              thread.id === localSelectedThreadId
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-zinc-700'
                            }`}
                          >
                            <div className="truncate">
                              {thread.title ||
                                `Thread ${thread.id.slice(0, 8)}`}
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="border-t border-zinc-100 p-1">
                        <button
                          type="button"
                          onClick={handleCreateNewThread}
                          disabled={createThreadState.isLoading}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <Plus size={14} />
                          {createThreadState.isLoading
                            ? 'Creating...'
                            : 'New Thread'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleRunDiscovery}
              disabled={isRunningDiscovery || isThreadsLoading}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isRunningDiscovery
                  ? 'bg-violet-100 text-violet-700 cursor-wait'
                  : 'bg-violet-600 text-white hover:bg-violet-700'
              }`}
            >
              <Sparkles size={14} />
              {isRunningDiscovery ? 'Running Discovery...' : 'Run AI Discovery'}
            </button>
          </div>

          {/* Content based on selected view */}
          {rightPanelView === 'chat' ? (
            <div className="flex-1 min-h-0 overflow-hidden">
              {localSelectedThreadId ? (
                <AgentChatThread
                  matterId={matterId}
                  threadId={localSelectedThreadId}
                  coverageId={coverageId}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  {isThreadsLoading
                    ? 'Loading chat...'
                    : 'Click "Run AI Discovery" to start analyzing documents'}
                </div>
              )}
            </div>
          ) : (
            /* Sources view */
            (() => {
              // Check if we have meaningful AI extraction context for client fields
              const hasExtractionContext =
                activeFieldDetails?.allExtractionContexts &&
                activeFieldDetails.allExtractionContexts.length > 0 &&
                activeFieldDetails.allExtractionContexts.some(
                  (ctx) =>
                    ctx.artifact_id ||
                    ctx.rationale ||
                    (ctx.excerpts?.length ?? 0) > 0,
                );

              // For client fields WITH extraction context, show the new AI Explanation pane
              if (sectionType === 'client' && hasExtractionContext) {
                return (
                  <div className="flex-1 min-h-0">
                    <AIExplanationPane
                      matterId={matterId}
                      fieldName={activeFieldDetails?.fieldName}
                      fieldValue={activeFieldDetails?.fieldValue}
                      isRequired={activeFieldDetails?.isRequired}
                      extractionContext={activeFieldDetails?.extractionContext}
                      allExtractionContexts={
                        activeFieldDetails?.allExtractionContexts
                      }
                      isFrozen={!!lockedFieldKey}
                    />
                  </div>
                );
              }

              // Fallback: Show EvidencePane for client fields without context,
              // or for vehicle/insurance fields (coverage mentions)
              const labelOverrideByKey: Record<string, string> = (() => {
                const map: Record<string, string> = {};
                // Insurance synthetic keys
                map['ins:carrier'] = 'Insurance Carrier';
                map['ins:insured'] = 'Insured Name';
                map['ins:policy'] = 'Policy Number';
                // Vehicle synthetic keys (per index)
                const count = coverage?.vehicles?.length ?? 0;
                for (let i = 0; i < count; i += 1) {
                  map[`vehicle:${i}:title`] = 'Vehicle';
                  map[`vehicle:${i}:year`] = 'Vehicle Year';
                  map[`vehicle:${i}:make`] = 'Vehicle Make';
                  map[`vehicle:${i}:model`] = 'Vehicle Model';
                  map[`vehicle:${i}:plate`] = 'Vehicle Plate';
                }
                return map;
              })();
              const initialSearchTerm = computeInitialSearchTerm(
                reviewedFields,
                activeFieldKey,
                coverage,
              );
              return (
                <div className="flex-1 min-h-0">
                  <EvidencePane
                    matterId={matterId}
                    activeFieldKey={activeFieldKey}
                    activeFieldSource={activeFieldSource}
                    requirementsById={requirementsById}
                    labelOverrideByKey={labelOverrideByKey}
                    initialSearchTerm={initialSearchTerm}
                    coverageMentions={coverage?.mentions ?? null}
                    sectionType={sectionType}
                    isFrozen={!!lockedFieldKey}
                  />
                </div>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
};

// Main exported component - wraps inner component with AgentChatProvider
export const ClaimReviewStep: React.FC<ClaimReviewStepProps> = ({
  matterId,
  ...rest
}) => {
  return (
    <EditorProvider>
      <AgentChatProvider>
        <ClaimReviewStepInner matterId={matterId} {...rest} />
      </AgentChatProvider>
    </EditorProvider>
  );
};
